// preferences.ts — UserPreferences and personalisation key unions
import type { AccountMenuShortcut } from './accountMenu';

// Accent colour preset key.
// 'default' = the current premium-purple tokens; 'teal' = the classic teal-HUD look.
// 'custom' = a user-picked colour (accentCustom, via the Appearance colour wheel).
// The named keys map to alternate brand colour sets (theme/presets/accents.ts).
export type AccentKey =
  | 'default'
  | 'teal'
  | 'sunset'
  | 'aurora'
  | 'ember'
  | 'mint'
  | 'magma'
  | 'custom';

// Surface/accent finish. 'flat' = solid (default); 'metallic' = gradient sheen on
// accent fills; 'glow' = stronger accent glow.
export type FinishKey = 'flat' | 'metallic' | 'glow';
export type OverlayGradientKey = 'none' | 'accent' | 'aurora' | 'sunset';

// Font family preset key.
// RETIRED as a control: the type system is fixed in theme/tokens.ts and
// applyPreferences never reads this. The key survives only so stored records
// written by older builds still parse; nothing in the UI offers it. Dropping
// the field is a stored-schema migration and is deliberately not done here.
export type FontKey = 'default' | 'humanist' | 'rounded' | 'mono-display';

// Text size step.
// Maps to a multiplier in theme/presets/textSize.ts (sm:0.92, md:1, lg:1.12, xl:1.25).
export type TextSizeKey = 'sm' | 'md' | 'lg' | 'xl';

// Legacy border-radius preset key. RETIRED as a stored pref — radius is now the numeric
// `radiusPx`. Kept only so prefsService can migrate old records (theme/presets/radius.ts
// maps sharp→8px, default→16px, round→26px).
export type RadiusKey = 'sharp' | 'default' | 'round';

// Post-timestamp display style (Settings → Language & Region).
// 'relative' = the server's compact string ("2h"); 'absolute' = a formatted
// local date-time derived from createdAt. Comments stay relative — the comment
// payload carries no createdAt.
export type TimestampStyleKey = 'relative' | 'absolute';

// Clock format for absolute timestamps (Settings → Language & Region).
export type ClockFormatKey = '12h' | '24h';

// Which feed tab Home lands on (Settings → Content Preferences). 'following'
// is the platform default — chronological-first is the anti-virality thesis;
// the algorithmic tabs are opt-in. Mirrors features/home FeedTabKey.
export type DefaultFeedTabKey = 'following' | 'for-you' | 'trending' | 'discover';

// Feed card density (Settings → Content Preferences). Viewer-side layout only —
// never filters or hides anyone's content.
export type FeedDensityKey = 'comfortable' | 'compact';
export type FeedOrderKey = 'default' | 'newest' | 'most-liked' | 'oldest';

// Background render tier. 'auto' picks high/low from device capability;
// 'high' = full CSS aurora (more layers + effects); 'low' = lightweight aurora;
// 'off' = flat background, no motion.
export type BackdropKey = 'auto' | 'high' | 'low' | 'off';

// Per-account user personalisation preferences.
// Stored per-account (keyed by handle) in localStorage via prefsStore.
// All fields required — the default values should always be set when creating a fresh record.
export interface UserPreferences {
  accountMenuShortcuts: AccountMenuShortcut[];
  themeMode: 'light' | 'dark';
  accent: AccentKey;
  /** User-picked accent colour (hex) — used when accent === 'custom'. */
  accentCustom: string;
  /** Accent/surface finish treatment. */
  finish: FinishKey;
  fontFamily: FontKey;
  textSize: TextSizeKey;
  /**
   * Card corner radius in px (0–48). Drives radius.lg exactly and scales sm/md by the
   * same ratio. Replaces the old sharp/default/round enum.
   */
  radiusPx: number;
  /** Lower bound of the corner-radius slider (the left input box). */
  radiusMinPx: number;
  /** Upper bound of the corner-radius slider (the right input box). */
  radiusMaxPx: number;
  /**
   * Card colour SEED (hex). Treated as hue + chroma only — its lightness is discarded
   * and re-solved into the theme's legible luminance band, so no pick can break text
   * contrast. Inert while cardColorStrength is 0.
   */
  cardColor: string;
  /** How far the base card surface is mixed toward cardColor (0–1). 0 = stock surface. */
  cardColorStrength: number;
  /** Directional key-light band across the card (0–1). 0 = matte. */
  cardSheen: number;
  /** Micro-roughness noise (0–1, quantised to 5 named stops). 0 = smooth. */
  cardGrain: number;
  /** Specular top-edge hairline (0–1). GATED behind cardSheen so it can't fake a bevel. */
  cardEdge: number;
  /** How far the card floats — drives its drop shadow (0–1). */
  cardElevation: number;
  /** Colour wash on cards and dialog surfaces; independent of glass and accent fills. */
  overlayGradient: OverlayGradientKey;
  overlayGradientStrength: number;
  postGradientStrength: number;
  panelGradientStrength: number;
  /** Whether the user's likes are publicly visible on their profile */
  likesPublic: boolean;
  /** Minimise animations and transitions */
  reduceMotion: boolean;
  /** Experimental decorative motion in page banners. Opt-in only. */
  bannerAnimations: boolean;
  /**
   * Accessibility: underline anchor links everywhere so they don't rely on
   * colour alone. Consumed by GlobalStyle via the theme's underlineLinks flag.
   */
  underlineLinks: boolean;
  /** Which feed tab Home lands on. Consumed by HomePage's initial tab state. */
  rememberFeedPosition: boolean;
  defaultFeedTab: DefaultFeedTabKey;
  /** Feed card density. Consumed by PostCard's container spacing. */
  feedDensity: FeedDensityKey;
  /** Order of posts within the selected Home tab; independent of followed discussions. */
  feedOrder: FeedOrderKey;
  /**
   * Post timestamps: relative ("2h") or absolute local date-time. Consumed by
   * formatPostTime (AuthorLink + HeroPostCard).
   */
  timestampStyle: TimestampStyleKey;
  /** 12-hour vs 24-hour clock for absolute timestamps (formatPostTime). */
  clockFormat: ClockFormatKey;
  /**
   * Accessibility: force opaque cards regardless of the Appearance glass
   * settings. Overrides (does not modify) cardsOverlay/panelSolidity — turning
   * it back off restores the user's glass configuration untouched. Consumed in
   * applyPreferences (gates cardsGlass).
   */
  reduceTransparency: boolean;
  /** Background render tier (auto/high/low/off). */
  backdrop: BackdropKey;
  /**
   * In-app background intensity (0.15–1.0) — how bold the Sundown Signal is
   * behind the feed. Auth always renders at full (1.0); this scales the shell.
   * Higher = more visible sun/grid (and more legibility pressure).
   */
  bgIntensity: number;
  /**
   * Cards overlay. true (default) = opaque cards that block the animated
   * background; false = glass cards (translucent + blur) that let it show through.
   */
  cardsOverlay: boolean;
  /** Glass panel opacity (alpha of the glassBg token), 0.15–0.95. Higher = more solid. */
  panelSolidity: number;
  /**
   * Glass blur radius in px (0–24). Drives the card `backdrop-filter: blur()`.
   * 0 = no frosting (pure transparency); higher = more frosted. Only active in
   * glass mode (cardsOverlay === false).
   */
  glassBlur: number;
  /**
   * Glass accent tint (0–0.6). Fraction the glass fill is mixed toward the active
   * accent colour — 0 = neutral void glass, higher = accent-tinted glass. Only
   * active in glass mode.
   */
  glassTint: number;
  /**
   * Glass saturation multiplier (1.0–1.8) applied to the `backdrop-filter` so the
   * colour behind the glass reads richer. 1.0 = untouched. Only active in glass mode.
   */
  glassSaturation: number;
}

export const PANEL_SOLIDITY_RANGE = { min: 0.15, max: 0.95, default: 0.8 } as const;

/** Glass blur radius (px). */
export const GLASS_BLUR_RANGE = { min: 0, max: 24, default: 12 } as const;
/** Glass accent-tint fraction (kept ≤ 0.6 so tinted glass never reads as a solid accent slab). */
export const GLASS_TINT_RANGE = { min: 0, max: 0.6, default: 0 } as const;
/** Glass backdrop saturation multiplier. */
export const GLASS_SATURATION_RANGE = { min: 1, max: 1.8, default: 1.2 } as const;

/** In-app background intensity range. Above ~0.55 the sun starts pressuring
 *  feed legibility — the Appearance slider marks that zone. */
export const BG_INTENSITY_RANGE = { min: 0.15, max: 1, default: 0.45, legibleMax: 0.55 } as const;

/** Clamp an intensity value into range. */
export function clampBgIntensity(n: number): number {
  const { min, max } = BG_INTENSITY_RANGE;
  return Math.min(max, Math.max(min, n));
}

/** Clamp a panel-solidity value into the valid [min, max] range. */
export function clampSolidity(n: number): number {
  const { min, max } = PANEL_SOLIDITY_RANGE;
  return Math.min(max, Math.max(min, n));
}

/** Clamp a glass-blur value (px) into range. */
export function clampGlassBlur(n: number): number {
  const { min, max } = GLASS_BLUR_RANGE;
  return Math.min(max, Math.max(min, n));
}

/** Clamp a glass accent-tint fraction into range. */
export function clampGlassTint(n: number): number {
  const { min, max } = GLASS_TINT_RANGE;
  return Math.min(max, Math.max(min, n));
}

/** Clamp a glass saturation multiplier into range. */
export function clampGlassSaturation(n: number): number {
  const { min, max } = GLASS_SATURATION_RANGE;
  return Math.min(max, Math.max(min, n));
}

/* ─── Card appearance ──────────────────────────────────────────────────────── */

/** Every card material scalar (strength / sheen / grain / edge / elevation) is 0–1. */
export const CARD_UNIT_RANGE = { min: 0, max: 1 } as const;
/** Default card elevation — reproduces the shipped elevation.sm shadow exactly. */
export const CARD_ELEVATION_DEFAULT = 0.5;
/** Default card colour seed (the dark base surface; inert at strength 0). */
export const CARD_COLOR_DEFAULT = '#0E141A';

/** Clamp a 0–1 card material scalar. */
export const clampUnit = (n: number): number => Math.min(1, Math.max(0, n));

/**
 * Corner radius, in px.
 * `hardMin`/`hardMax` are structural limits the theme always enforces; `defaultMin`/
 * `defaultMax` seed the user-editable slider bounds. 48px is the ceiling because
 * radius.sm/md scale by the same ratio (24/36px at 48) and 24px already exceeds the
 * card's 16px padding.
 */
export const RADIUS_PX_RANGE = {
  hardMin: 0,
  hardMax: 48,
  // 1.25.00: 16 → 8. One generous radius on every card, button, chip, tab and
  // avatar is a named generated-UI tell, and at 16 the sm/md/lg steps (8/12/16)
  // are too close to read as a hierarchy. Only the DEFAULT moves — the base
  // radius tokens are untouched, so every account that has already dragged the
  // slider keeps exactly the value it stored.
  default: 8,
  defaultMin: 0,
  defaultMax: 32,
} as const;

/** The slider track can never invert or collapse. */
export const RADIUS_MIN_SPAN = 4;

/** Clamp to the STRUCTURAL px limits (never the user's soft bounds). */
export const clampRadiusHard = (n: number): number =>
  Math.min(RADIUS_PX_RANGE.hardMax, Math.max(RADIUS_PX_RANGE.hardMin, Math.round(n)));

/**
 * Repair a bound pair. `anchor` names the box the user just edited, so the OTHER one
 * moves — editing "min" pushes max up, editing "max" pushes min down.
 */
export function clampRadiusBounds(
  lo: number,
  hi: number,
  anchor: 'min' | 'max' = 'max',
): [number, number] {
  let a = clampRadiusHard(lo);
  let b = clampRadiusHard(hi);
  if (b - a < RADIUS_MIN_SPAN) {
    if (anchor === 'min') b = clampRadiusHard(a + RADIUS_MIN_SPAN);
    else a = clampRadiusHard(b - RADIUS_MIN_SPAN);
  }
  return [a, b];
}
